/**
 * config/onboardingActions.js — куда ведёт CTA каждого шага онбординга.
 *
 * Зеркало backend/config/onboardingSteps.js: там — какие шаги существуют и кому
 * показываются, здесь — что происходит по нажатию. Состав и порядок шагов фронт
 * НЕ решает: их присылает GET /api/onboarding/checklist уже отфильтрованными по
 * роли, стране и тумблерам модулей. Продублированная здесь фильтрация неминуемо
 * разъедется с серверной.
 *
 * Ключ без записи в этой карте ведёт на Дашборд — шаг всё равно покажется, но
 * кнопка окажется бесполезной, поэтому новый шаг заводим сразу в обоих файлах.
 *
 * i18n: подписи — onboarding.steps.{key}.label / .hint / .cta.
 * Шаг с docByCountry берёт label с суффиксом типа документа (personalDoc.js):
 * onboarding.steps.doc.label.emirates_id / .passport_ru.
 */

/**
 * @param {object} ctx
 *   navigate  — из useNavigate()
 *   handlers  — колбэки Дашборда, если он умеет открыть модалку сам
 *               ({ onTaskCreate, onClientAdd, onPropertyAdd, onWorkerInvite, onLogoUpload })
 *   tutorial  — { taskId, propertyId } для диплинка в тест-Работу
 */
export function onboardingStepAction(key, { navigate, handlers = {}, tutorial = {} }) {
  const go = (path, state) => navigate(path, state ? { state } : undefined);

  // Тест-Работа открывается раскрытой внутри Объекта (PropertyDetailPage →
  // openTaskId). Если Объект неизвестен — уводим в общий список, а не в никуда.
  const openTutorialWork = () => {
    if (tutorial.propertyId) {
      go(`/properties/${tutorial.propertyId}`, tutorial.taskId ? { openTaskId: tutorial.taskId } : null);
    } else {
      go('/tasks');
    }
  };

  switch (key) {
    // ── Настройка тенанта ─────────────────────────────────────────────────
    case 'logo':          return handlers.onLogoUpload    ? handlers.onLogoUpload()    : go('/settings');
    // Кнопка добавления на экран — в Профиле → Личное (доступна всем ролям).
    case 'home_screen':   return go('/profile');
    case 'client':        return handlers.onClientAdd     ? handlers.onClientAdd()     : go('/contacts', { openNew: true });
    case 'property':      return handlers.onPropertyAdd   ? handlers.onPropertyAdd()   : go('/properties', { openNew: true });
    case 'worker':        return handlers.onWorkerInvite  ? handlers.onWorkerInvite()  : go('/settings?tab=team');
    case 'task':          return handlers.onTaskCreate    ? handlers.onTaskCreate()    : go('/properties');
    case 'price_list':    return go('/settings?tab=price');
    case 'email_sender':  return go('/settings?tab=integrations');
    case 'zoho':          return go('/settings?tab=integrations');
    // Trade License и VAT-сертификат живут в Сейфе организации — это вкладка
    // документов команды, отдельного роута у Сейфа нет.
    case 'trade_license': return go('/settings?tab=tdocs');
    case 'vat':           return go('/settings?tab=tdocs');
    // AMC-контракт заводится на карточке Объекта (AmcSection), отдельного
    // списка контрактов в продукте нет — ведём в Объекты.
    case 'amc':           return go('/properties');

    // ── Личное ────────────────────────────────────────────────────────────
    case 'profile':    return go('/profile');
    case 'doc':        return go('/profile', { tab: 'documents' });
    case 'task_own':   return handlers.onTaskCreate ? handlers.onTaskCreate() : go('/properties');
    case 'task_photo': return go('/tasks');
    case 'expense':    return go('/expenses');
    case 'work_fact':  return go('/projects');

    // ── Туториал Работника ────────────────────────────────────────────────
    case 'test_work':
    case 'expense_tutorial':
      return openTutorialWork();

    default:
      return go('/');
  }
}

/**
 * i18n-ключ подписи шага. Для странового шага (docByCountry) добавляем суффикс
 * типа документа: в ОАЭ это Emirates ID, в РФ — главный разворот паспорта.
 */
export function stepLabelKey(step, docType) {
  return step.docByCountry
    ? `onboarding.steps.${step.key}.label.${docType}`
    : `onboarding.steps.${step.key}.label`;
}
